import React from 'react';
import { useCaptureDetailsFlow } from '../../../hooks/useCaptureDetailsFlow';
import { Button, TextInput, Notification } from '@osttra/ds-react';
import { AUTHORISED_USERS_NOTICE, PRIVACY_NOTICE } from '../../constants/Constants';
import OsttraBg from '../../../images/osttra-bg.png';
import OsttraBlack from '../../../images/OsttraBlack.svg';
import './CaptureDetails.scss';

const CaptureDetails: React.FC = () => {
  const {
    step,
    username,
    email,
    setEmail,
    otp,
    timer,
    canResend,
    error,
    isLoading,
    isResending,
    isLockedOut,
    handleChange,
    handlePaste,
    handleContinue,
    handleResend,
    canContinue,
    otpStatus,
    showSkipPolicy,
    setShowSkipPolicy,
    handleSkip,
    isSkipLoading,
    onSendOtpClick,
    onChangeEmail,
  } = useCaptureDetailsFlow();

  const renderSkipLimitNotification = () => {
    if (!otpStatus || !otpStatus.skipAllowed || !showSkipPolicy) {
      return null;
    }

    const skippedAttempts = otpStatus.totalAttempts - otpStatus.skipsRemaining;

    return (
      <Notification isVisible subtle type="info" onDismiss={() => setShowSkipPolicy(false)}>
        <strong>Skip Limit Policy:</strong> You can skip this setup {otpStatus.totalAttempts} times in total. You have
        skipped <strong>{skippedAttempts}</strong> time
        {skippedAttempts !== 1 ? 's' : ''}. If skipped{' '}
        {otpStatus.totalAttempts === 2
          ? 'twice'
          : otpStatus.totalAttempts === 3
          ? 'three times'
          : `${otpStatus.totalAttempts} times`}
        , you will be forced to verify.
      </Notification>
    );
  };

  return (
    <div className="capture-details-wrapper">
      <img src={OsttraBg} alt="osttra-background" className="osttra-background" />

      <div className="capture-details-container">
        <img src={OsttraBlack} className="brand-header-logo" alt="OSTTRA background" />

        <main className="capture-details-body">
          {step === 'EMAIL' && (
            <>
              <h2>Register corporate email</h2>
              <p className="capture-instruction">
                Your Traiana profile is missing a registered email address. Please supply a valid corporate address to
                continue.
              </p>
              <div className="otp-input-fields">
                <TextInput label="Username" value={username} onChange={() => {}} disabled size="large" />

                <TextInput
                  type="email"
                  label="Corporate email address"
                  placeholder="firstname.lastname@domain.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  size="large"
                />

                {error && (
                  <Notification isVisible subtle type="error" className="otp-error-banner">
                    {error}
                  </Notification>
                )}
                <div className="action-buttons-row">
                  {otpStatus?.skipAllowed && (
                    <Button onClick={handleSkip} loading={isSkipLoading} disabled={isLoading || isSkipLoading}>
                      Skip for now ({otpStatus.skipsRemaining} left)
                    </Button>
                  )}
                  <Button type="primary" onClick={onSendOtpClick} disabled={!email} loading={isLoading}>
                    Send code
                  </Button>
                </div>

                {renderSkipLimitNotification()}
              </div>
            </>
          )}

          {step === 'OTP' && (
            <>
              <h2>Enter security code</h2>
              <div className="capture-instruction">
                A 6-digit verification code has been emailed to:
                <div className="otp-user-email">{email}</div>
              </div>
              <div className="otp-input-fields">
                <div className="otp-input t-flex t-item-center">
                  <TextInput
                    label="Enter security code"
                    placeholder="6 digit code"
                    value={otp}
                    onChange={(e) => handleChange(e.target.value)}
                    onPaste={handlePaste}
                    disabled={isLockedOut || isLoading}
                    maxLength={6}
                    aria-label="Security code"
                    size="small"
                  />
                  <Button
                    className={`resend-button ${canResend ? 't-primary' : ''}`}
                    variant="ghost"
                    onClick={handleResend}
                    disabled={!canResend || isResending || isLockedOut}
                    icon={'clock'}
                  >
                    {canResend ? 'Resend code' : `Resend code in ${timer}s`}
                  </Button>
                </div>
                {error && (
                  <Notification isVisible subtle type="error" className="otp-error-banner">
                    {error}
                  </Notification>
                )}

                <div className="action-buttons-row">
                  {otpStatus?.skipAllowed && (
                    <Button onClick={handleSkip} loading={isSkipLoading} disabled={isLoading || isSkipLoading}>
                      Skip for now ({otpStatus.skipsRemaining} left)
                    </Button>
                  )}
                  <Button type="primary" onClick={handleContinue} disabled={!canContinue} loading={isLoading}>
                    Verify
                  </Button>
                </div>

                {renderSkipLimitNotification()}

                <div className="back-link-wrapper">
                  <Button className="change-email-btn" icon="arrow-left" variant="ghost" onClick={onChangeEmail}>
                    Change email
                  </Button>
                </div>
              </div>
            </>
          )}

          <div className="capture-legal-notice">
            <p>{AUTHORISED_USERS_NOTICE}</p>
            <p>{PRIVACY_NOTICE}</p>
          </div>
        </main>

        <footer className="capture-details-footer">
          <span className="footer-brand">
            <img src={OsttraBlack} alt="OSTTRA Logo" />
          </span>
          <span className="footer-tagline">
            THE GLOBAL <strong>POSTTRADE</strong> NETWORK
          </span>
        </footer>
      </div>
    </div>
  );
};

export default CaptureDetails;
